import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bulkUpdateLeaves, getAdminLeaveRequests, getCCLeaveRequests, getEmpLeaveRequests, getLeaveSummaryReport, getSingleEmpLeave, raiseLeverequest, updateEmpLeaveStatus, updateLeaveStatus, withdrawLeaveRequest } from '../api';

import { toast } from 'react-toastify';

export const useSingleEmpLeaveDetails = (id) => {
  return useQuery({
    queryKey: ['singleEmployeeLeave', id], // Unique query key for caching
    queryFn: async ({ queryKey }) => {
      const data = await getSingleEmpLeave({ queryKey });
      if (Array.isArray(data)) {
        return data.sort((a, b) => (b.id || 0) - (a.id || 0));
      }
      return data;
    },
    enabled: !!id, // Ensures the query only runs if `id` is valid
  });
};

export const useEmpLeaveRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ formData }) => raiseLeverequest({ formData }),
    onSuccess: (data, variables) => {
      // Invalidate only leave-specific data to avoid heavy refetch of the full employee list
      queryClient.invalidateQueries(['singleEmployeeLeave']);
      // Also refresh the employee's attendance summaries so the LOP-days count
      // (shown on the Attendance page's payroll summary card, which now doubles
      // as an Apply-for-Leave shortcut) reflects the new request.
      queryClient.invalidateQueries({ queryKey: ['employeeCycleAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['employeeAttendanceHistory'] });
    },
    onError: (error) => {
      console.error('Error submitting leave request:', error);
      toast.error(
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to submit leave request. Please try again.',
        {
          autoClose: 4000,
          position: 'top-right',
        }
      );
    },
  });
};

export const useAdminLeaveRequests = () => {
  return useQuery({
    queryKey: ['adminLeaveRequests'], // Unique query key for caching
    queryFn: getAdminLeaveRequests,  // API function to fetch leave requests
  });
};

export const useLeaveCCrequests = (id) => {
  return useQuery({
    queryKey: ['LeaveCCRequests', id], // Unique query key for caching
    queryFn: getCCLeaveRequests,  // API function to fetch leave requests
    enabled: !!id,
  });
}

export const useUpdateLeaveStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leave_request_id, status, status_reason }) => updateLeaveStatus({ leave_request_id, status, status_reason }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['adminLeaveRequests']);
      queryClient.invalidateQueries(['singleEmployee']);
      const msg = variables.status === 'ACCEPTED'
        ? '✅ Leave approved successfully.'
        : '❌ Leave declined.';
      toast.success(msg, { autoClose: 4000, position: 'top-right' });
    },
    onError: (error) => {
      console.error('Error updating status:', error);
      const backendMsg = error?.response?.data?.error || error?.response?.data?.detail;
      toast.error(backendMsg || 'Failed to update leave status. Please try again.', { autoClose: 5000 });
    },
  });
};
export const useLeaveRequestsforme = (id) => {
  return useQuery({
    // Scope the cache per employee. A static key shared the same cache entry
    // across all employees, so one user could see another user's leave data.
    queryKey: ['LeaveRequestsEmp', id],
    queryFn: () => getEmpLeaveRequests(id),  // API function to fetch leave requests
    enabled: !!id,
  });
};
export const useUpdateEmpLeaveRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leave_request_id, status, status_reason }) => updateEmpLeaveStatus({ leave_request_id, status, status_reason }),
    onSuccess: (data, variables) => {
      // Object form for React Query v5; prefix-matches every ['LeaveRequestsEmp', <id>] entry.
      queryClient.invalidateQueries({ queryKey: ['LeaveRequestsEmp'] });
      const msg = variables.status === 'ACCEPTED'
        ? '✅ Leave approved successfully.'
        : '❌ Leave declined.';
      toast.success(msg, { autoClose: 4000, position: 'top-right' });
    },
    onError: (error) => {
      console.error('Error updating status:', error);
      const backendMsg = error?.response?.data?.error || error?.response?.data?.detail;
      toast.error(backendMsg || 'Failed to update leave status. Please try again.', { autoClose: 5000 });
    },
  });
};

// Employee withdraws/cancels their own leave. Restores balance server-side, so
// refresh BOTH the leave history list and the employee record (the balance pills
// read total/sick/casual from useGetSingleEmployee -> ['singleEmployee', id]).
export const useWithdrawLeaveRequest = (employeeId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leave_request_id, employee_no, reason }) =>
      withdrawLeaveRequest({ leave_request_id, employee_no, reason }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['singleEmployeeLeave'] });
      queryClient.invalidateQueries({ queryKey: ['singleEmployee', employeeId] });
      toast.success(data?.message || 'Leave request updated.', { autoClose: 4000, position: 'top-right' });
    },
    onError: (error) => {
      const backendMsg = error?.response?.data?.error || error?.response?.data?.detail;
      toast.error(backendMsg || 'Failed to withdraw leave request. Please try again.', { autoClose: 5000 });
    },
  });
};

// HR/admin cancels an employee's leave from the admin Leave Requests page.
// Restores the balance server-side (actor='HR'), so refresh the admin list AND
// the employee record so the balance reflects immediately.
export const useAdminCancelLeave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leave_request_id, reason }) =>
      withdrawLeaveRequest({ leave_request_id, reason, actor: 'HR' }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['adminLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['singleEmployee'] });
      toast.success(data?.message || 'Leave cancelled.', { autoClose: 4000, position: 'top-right' });
    },
    onError: (error) => {
      const backendMsg = error?.response?.data?.error || error?.response?.data?.detail;
      toast.error(backendMsg || 'Failed to cancel leave. Please try again.', { autoClose: 5000 });
    },
  });
};

export const useLeaveSummaryReport = () => {
  return useQuery({
    queryKey: ['leaveSummaryReport'],
    queryFn: getLeaveSummaryReport,
  });
};

export const useBulkUpdateLeaves = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkUpdateLeaves,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leaveSummaryReport'] });
      toast.success(data.message || 'Leaves updated successfully');
    },
    onError: (error) => {
      const msg = error.response?.data?.error || "Failed to update leaves";
      toast.error(msg);
      console.error(error);
    }
  });
};